import {
	FunctionComponent,
	ReactNode,
	useCallback,
	useMemo,
	useState,
} from "react";
import { Button, Icon, Tooltip, TooltipPortal } from "@animech/ui";
import * as Styled from "./multi-edit-pen-panel.style";
import { useUiData } from "src/ui/data";

type ColorOption = {
	value: string;
	image: string;
	label?: ReactNode;
};

type EditableProduct = {
	id: string;
	label: string;
	domain: ColorOption[];
};

export const MultiEditPenPanel: FunctionComponent = () => {
	const { localizer, visualization } = useUiData();
	const { onToggleMultiEditPenPanel } = visualization;

	// Calculated per app
	const editableProducts: EditableProduct[] = useMemo(() => {
		return [];
	}, []);

	// Calculated per app
	const setColorForProperties = useCallback(
		(_productId: string, _color: string) => {
			return;
		},
		[],
	);

	const [closedAccordionIds, setClosedAccordionIds] = useState<Set<string>>(
		() => new Set(),
	);

	const toggleAccordionClosed = useCallback((id: string) => {
		setClosedAccordionIds((prev) => {
			const next = new Set(prev);
			if (next.has(id)) {
				next.delete(id);
			} else {
				next.add(id);
			}
			return next;
		});
	}, []);

	return (
		<Styled.MultiEditPenPanel>
			<Styled.EditableProducts>
				{editableProducts.map((ep) => {
					const { id, domain, label } = ep;
					const isClosed = closedAccordionIds.has(id);
					return (
						<Styled.Accordion key={id} open={!isClosed}>
							<Styled.AccordionHeader onClick={() => toggleAccordionClosed(id)}>
								<Styled.ProductLabel>{label}</Styled.ProductLabel>
								<Styled.AccordionHeaderButton>
									<Icon
										name={isClosed ? "chevron-down-thin" : "chevron-up-thin"}
										scale={0.8}
									/>
								</Styled.AccordionHeaderButton>
							</Styled.AccordionHeader>
							<Styled.AccordionContent>
								<ColorSelect
									options={domain}
									onChange={(color) => setColorForProperties(id, color)}
								/>
							</Styled.AccordionContent>
						</Styled.Accordion>
					);
				})}
			</Styled.EditableProducts>

			<Styled.CloseButtonContainer>
				<Button
					type="block"
					appearance="ghost"
					onClick={() => onToggleMultiEditPenPanel(false)}
				>
					{localizer.localizeTerm("close")}
				</Button>
			</Styled.CloseButtonContainer>
		</Styled.MultiEditPenPanel>
	);
};

type ColorSelectProps = {
	options: ColorOption[];
	onChange: (color: string) => void;
};
export function ColorSelect({
	options,
	onChange: onColorChangeFromProps,
}: ColorSelectProps) {
	const [activeColor, setActiveColor] = useState<string | null>(null);

	const onClick = (color: string) => {
		setActiveColor(color);
		onColorChangeFromProps(color);
	};

	return (
		<Styled.ColorSelect>
			{options.map((opt) => {
				const checked = opt.value === activeColor;

				return (
					<TooltipPortal
						key={opt.value}
						anchorElement={
							<Styled.SwatchButton
								type="button"
								role="radio"
								aria-checked={checked}
								onClick={() => onClick(opt.value)}
								active={checked}
							>
								<Styled.SwatchImage src={opt.image} draggable={false} />
							</Styled.SwatchButton>
						}
						toggleEvent="hover"
					>
						<Tooltip>{opt.label ?? opt.value}</Tooltip>
					</TooltipPortal>
				);
			})}
		</Styled.ColorSelect>
	);
}
